import {HttpClient, HttpParams} from '@angular/common/http';
import {Injectable, inject} from '@angular/core';
import {
    IEducation,
    IPagedData,
    IPagedSearch,
    ISecurityClearance,
    IUser,
    IUserActivate,
    IUserActivitySearch,
    IUserActivitySummary,
    IUserCreate,
} from '@evencel/models';
import {Observable, take} from 'rxjs';

export const USER_URL = 'users';

@Injectable({providedIn: 'root'})
export class UserService {
    httpClient = inject(HttpClient);

    post(data: IUserCreate): Observable<IUser> {
        return this.httpClient.post<IUser>(USER_URL, data);
    }

    patch(data: IUser): Observable<IUser> {
        const url = `${USER_URL}/${data._id}`;
        return this.httpClient.patch<IUser>(url, data);
    }

    activate(data: IUserActivate): Observable<IUser> {
        const {active} = data;
        const url = `${USER_URL}/${data._id}/activate`;
        return this.httpClient.patch<IUser>(url, {active});
    }

    delete(id: string): Observable<void> {
        return this.httpClient.delete<void>(`${USER_URL}/${id}`);
    }

    restore(id: string): Observable<void> {
        return this.httpClient.delete<void>(`${USER_URL}/${id}/restore`);
    }

    private searchParams(data: IPagedSearch) {
        const {active, direction} = data.sort;
        const {pageIndex, pageSize} = data.page;
        let params = new HttpParams();
        params = params.append('pageIndex', `${pageIndex}`);
        params = params.append('pageSize', `${pageSize}`);
        params = params.append('sort', active);
        params = params.append('order', direction);
        params = params.append('projection', data.projection || 'dataTable');
        return params;
    }

    search(data: IPagedSearch): Observable<IPagedData<IUser>> {
        const params = this.searchParams(data);
        return this.httpClient.post<IPagedData<IUser>>(`${USER_URL}/search`, data.condition, {params});
    }

    searchEmail(data: IPagedSearch): Observable<IPagedData<IUser>> {
        const params = this.searchParams(data);
        return this.httpClient.post<IPagedData<IUser>>(`${USER_URL}/search-email`, data.condition, {params});
    }

    addContinuingEducation(user: IEducation) {
        const url = `${USER_URL}/0/continuing-education`;
        return this.httpClient.post<IUser>(url, user);
    }

    addSecurityClearence(user: ISecurityClearance) {
        const url = `${USER_URL}/0/security-clearance`;
        return this.httpClient.post<IUser>(url, user);
    }

    getSecurityClearence(userId = '0') {
        const url = `${USER_URL}/${userId}/security-clearance`;
        return this.httpClient.get<IUser>(url);
    }

    getSecurityClearenceHistory(entityId: string) {
        const url = `${USER_URL}/0/security-clearance/history/${entityId}`;
        return this.httpClient.get<IUser>(url);
    }

    updateContinuingEducation(education: IEducation) {
        const url = `${USER_URL}/0/continuing-education/${education._id}`;
        return this.httpClient.patch<IUser>(url, education);
    }

    deleteContinuingEducation(education: IEducation) {
        const url = `${USER_URL}/0/continuing-education/${education._id}`;
        return this.httpClient.delete<IUser>(url);
    }

    deleteSecurityClearence(education: ISecurityClearance) {
        const url = `${USER_URL}/0/security-clearance/${education._id}`;
        return this.httpClient.delete<IUser>(url).pipe(take(1));
    }

    getOne(id: string, projection = 'form'): Observable<IUser> {
        let params = new HttpParams();
        params = params.append('projection', projection);
        const url = `${USER_URL}/${id}`;
        return this.httpClient.get<IUser>(url, {params});
    }

    get(data: IPagedSearch): Observable<IPagedData<IUser>> {
        const params = this.searchParams(data);
        return this.httpClient.get<IPagedData<IUser>>(USER_URL, {params});
    }

    getUserGeneralInfo(id: string): Observable<IUser> {
        const url = `${USER_URL}/${id}/view`;
        return this.httpClient.get<IUser>(url);
    }

    getUserBasedActivityStatusCount(payload: IUserActivitySearch, userId?: string): Observable<IUserActivitySummary[]> {
        const url = `${USER_URL}/${userId}/user-activity-summary`;
        return this.httpClient.post<IUserActivitySummary[]>(url, payload);
    }
}
